import React from 'react';
import { TileData, Position, VikingData } from '../types/GameTypes';
import './TileComponent.css';

interface TileComponentProps {
  tile: TileData | null;
  position: Position;
  onClick?: () => void;
  vikings?: VikingData[];
  onVikingClick: (viking: VikingData) => void;
  attackableVikings: VikingData[];
  selectedViking: VikingData | null;
  initiateCombat: (attackerId: string, defenderId: string) => void;
  isHighlighted?: boolean;
}

const TileComponent: React.FC<TileComponentProps> = ({ tile, position, onClick, vikings = [], onVikingClick, attackableVikings, selectedViking, initiateCombat, isHighlighted = false }) => {
  if (!tile) {
    return (
      <div className="tile empty-tile">
        <span className="coordinates">{position.x},{position.y}</span>
      </div>
    );
  }

  const getTileClass = () => {
    let baseClass = 'tile';
    if (isHighlighted) baseClass += ' highlighted';
    if (!tile.revealed) return `${baseClass} fog-tile`;
    if (attackableVikings.some(v => v.position.x === position.x && v.position.y === position.y)) baseClass += ' attackable';

    switch (tile.type) {
      case 'starting': return `${baseClass} starting-tile`;
      case 'resource': return `${baseClass} resource-tile ${tile.resourceType}`;
      case 'standard': return `${baseClass} standard-tile ${tile.terrain}`;
      default: return baseClass;
    }
  };

  const getTileContent = () => {
    if (!tile.revealed) return <><span className="fog-icon">🌫️</span><span className="coordinates">{tile.x},{tile.y}</span></>;
    switch (tile.type) {
      case 'starting': return <><span className="tile-icon">🏠</span><span className="player-slot">P{tile.playerSlot}</span><span className="coordinates">{tile.x},{tile.y}</span></>;
      case 'resource': return <><span className="tile-icon">{getResourceIcon(tile.resourceType)}</span><span className="resource-info">+{tile.goldPerTurn}💰</span><span className="coordinates">{tile.x},{tile.y}</span></>;
      case 'standard': return <><span className="tile-icon">{getTerrainIcon(tile.terrain)}</span><span className="movement-cost">⚡{tile.movementCost}</span><span className="coordinates">{tile.x},{tile.y}</span></>;
      default: return <span className="coordinates">{tile.x},{tile.y}</span>;
    }
  };

  const getResourceIcon = (resourceType?: string) => {
    switch (resourceType) {
      case 'village': return '🏘️';
      case 'farmland': return '🌾';
      case 'mine': return '⛏️';
      case 'port': return '⚓';
      default: return '💰';
    }
  };

  const getTerrainIcon = (terrain?: string) => {
    switch (terrain) {
      case 'grassland': return '🌱';
      case 'forest': return '🌲';
      case 'mountain': return '⛰️';
      case 'swamp': return '🌿';
      default: return '🌍';
    }
  };

  const getPlayerColor = (playerSlot: number) => {
    const colors = ['', '#e74c3c', '#3498db', '#27ae60', '#f39c12', '#9b59b6', '#1abc9c'];
    return colors[playerSlot] || '#95a5a6';
  };

  return (
    <div className={getTileClass()} onClick={onClick} title={`${tile?.type || 'empty'} tile at (${position.x}, ${position.y})`}>
      {getTileContent()}
      {vikings.length > 0 && (
        <div className="vikings-on-tile">
          {vikings.slice(0, 4).map((viking, index) => {
            const isAttackable = attackableVikings.some(v => v.id === viking.id);
            return (
              <div key={viking.id} className="viking-on-tile-container" style={{ top: `${20 + (index % 2) * 25}%`, left: `${20 + Math.floor(index / 2) * 30}%` }}>
                <div
                  className={`viking-on-tile ${viking.isAlive ? 'alive' : 'dead'}`}
                  style={{ backgroundColor: getPlayerColor(viking.playerSlot) }}
                  title={`${viking.playerId} (P${viking.playerSlot}) - HP: ${viking.health}/${viking.maxHealth}`}
                  onClick={(e) => { e.stopPropagation(); onVikingClick(viking); }}
                >
                  {viking.isAlive ? '🧔' : '💀'}
                </div>
                {isAttackable && selectedViking && (
                  <button className="attack-button-tile" onClick={(e) => { e.stopPropagation(); initiateCombat(selectedViking.id, viking.id); }}>
                    ⚔️
                  </button>
                )}
              </div>
            );
          })}
          {vikings.length > 4 && <div className="viking-overflow">+{vikings.length - 4}</div>}
        </div>
      )}
    </div>
  );
};

export default TileComponent;
